import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex } from "../../../components/Flex";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { Info } from "../../../components/Info";

import { FontSize, Text } from "../../../components/Text";
import { SettingsWrapper } from "../components/SettingsWrapper";
import { chatbotActions } from "../../../redux/chatbot/chatbot.slice";
import { chatbotSliceSelectors } from "../../../redux/chatbot/chatbot.selector";
import { AtiraEmpty } from "../../../components/AtiraEmpty";
import styled from "styled-components";
import { Spacing } from "../../../theme/Spacing";
import { Rounded } from "../../../theme/Rounded";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { faRobot } from "@fortawesome/free-solid-svg-icons/faRobot";
import { TextWithTruncated } from "../../../components/TextWithTruncated";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { Tooltip } from "../../../utils/local-antd";
import { Button } from "../../../components/Button";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons/faPenToSquare";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { Chatbot } from "../../../model/chatbot/Chatbot";
import { AtiraToast } from "../../../utils/AtiraToast";
import SettingsAIChatBotShareDrawer from "../components/chatbot/share/SettingsAIChatBotShareDrawer";
import { faShare } from "@fortawesome/free-solid-svg-icons/faShare";
import { WarningModal } from "../../../components/WarningModal";
import { TestIDs } from "../../../tests/types/testIds";
import { SettingsAIChatBotCreateEditDrawer } from "../components/chatbot/SettingsAIChatBotCreate";
import { formActions } from "../../../redux/forms/form.slice";
import { useDrawersContext } from "../../tasks/drawers-context";
import { AtiraSpacePlan } from "../../../model/plan/Plan";
import { AtiraSpacePlanType } from "../../../model/plan/types/AtiraSpacePlanType.enum";

const BotCard = styled(Flex)`
  flex-direction: column;
  padding: ${Spacing.m};
  border-radius: ${Rounded.md};
  background: ${({ theme }) => theme.main};
  position: relative;
  overflow: hidden;
  width: 100%;
`;

const Name = styled(TextWithTruncated)`
  font-weight: 700;
  font-size: calc(${FontSize.s} + 0.15rem);
  color: ${({ theme }) => theme.white};
`;

const Description = styled(TextWithTruncated)`
  font-size: ${FontSize.s};
  color: ${({ theme }) => theme.sub};
  margin-top: ${Spacing.xs};
`;

const IconActionButton = styled(Button)<{ $isDanger?: boolean }>`
  background: transparent;
  padding: ${Spacing.s};
  margin: 0;
  color: ${({ theme }) => theme.sub};

  &:hover {
    background: ${({ theme }) => theme.lightSub};
    color: ${({ theme, $isDanger }) =>
      theme[$isDanger ? "danger" : "lightMain"]};
  }
`;

const ActionsRow = styled(Flex)`
  margin-top: ${Spacing.m};
  justify-content: flex-end;
  gap: ${Spacing.s};
`;

export type SettingsAIChatbotContextType = {
  createEditDrawerOpen: boolean;
  toggleCreateEditDrawerOpen: () => void;
  shareDrawerOpen: boolean;
  toggleShareDrawerOpen: () => void;
  currentBot: Chatbot | null;
  setCurrentBot: React.Dispatch<React.SetStateAction<Chatbot | null>>;
};

const SettingsAIChatbotContext =
  React.createContext<SettingsAIChatbotContextType>({
    createEditDrawerOpen: false,
    toggleCreateEditDrawerOpen: () => {},
    shareDrawerOpen: false,
    toggleShareDrawerOpen: () => {},
    currentBot: null,
    setCurrentBot: () => {},
  });

export const useSettingsAIChatbotContext = () => {
  const context = useContext(SettingsAIChatbotContext);

  if (!context) {
    throw new Error(
      "useSettingsAIChatbotContext must be used within a SettingsAIChatbotContextProvider",
    );
  }

  return context;
};

export const SettingsAIChatBot = () => {
  const [createEditDrawerOpen, setCreateEditDrawerOpen] = useState(false);
  const [shareDrawerOpen, setShareDrawerOpen] = useState(false);
  const [currentBot, setCurrentBot] = useState<Chatbot | null>(null);

  const toggleShareDrawerOpen = () =>
    setShareDrawerOpen((prevState) => !prevState);
  const toggleCreateEditDrawerOpen = () =>
    setCreateEditDrawerOpen((prev) => !prev);

  return (
    <SettingsAIChatbotContext.Provider
      value={{
        createEditDrawerOpen,
        toggleCreateEditDrawerOpen,
        shareDrawerOpen,
        toggleShareDrawerOpen,
        currentBot,
        setCurrentBot,
      }}
    >
      <SettingsAIChatBotInternal />
    </SettingsAIChatbotContext.Provider>
  );
};

const SettingsAIChatBotInternal: React.FC = () => {
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteBotModalOpen, setDeleteBotModalOpen] = useState(false);

  const chatbots = useAppSelector(chatbotSliceSelectors.selectAllChatbots);
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const plan = user?.plan || ({} as AtiraSpacePlan);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    toggleCreateEditDrawerOpen,
    toggleShareDrawerOpen,
    setCurrentBot,
    currentBot,
  } = useSettingsAIChatbotContext();
  const { setSubToUnlock } = useDrawersContext();

  const canCreateChatBot = plan.type === AtiraSpacePlanType.PLAN_2;

  const onCreate = () => {
    setCurrentBot(null);
    toggleCreateEditDrawerOpen();
  };

  const onEdit = (chatbot: Chatbot) => {
    setCurrentBot(chatbot);
    toggleCreateEditDrawerOpen();
  };

  const onDelete = async () => {
    if (!currentBot) {
      return;
    }

    try {
      setDeleteLoading(true);
      await dispatch(
        chatbotActions.deleteChatbot({ chatbotId: currentBot!._id, userId }),
      ).unwrap();
      await dispatch(chatbotActions.getChatbots({ userId })).unwrap();
      setDeleteBotModalOpen(false);
      AtiraToast.success(t("settings.aiChatbot.delete.success"));
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    dispatch(chatbotActions.getChatbots({ userId }));
    dispatch(formActions.getForms({ userId }));
  }, [dispatch, userId]);

  return (
    <SettingsWrapper>
      <Flex gap="l" flexDirection="column">
        <Text fontWeight={"bold"}>
          {t("settings.aiChatbot.subheader.title")}
        </Text>

        <Info info={t("settings.aiChatbot.info")} />
      </Flex>

      {chatbots?.length ? (
        <Flex flexDirection="column" gap="m">
          {chatbots.map((chatbot) => (
            <BotCard
              key={chatbot._id}
              dataTestId={TestIDs.chatbot.list.card(chatbot._id)}
            >
              <Flex alignItems="center" gap="m">
                <Flex alignItems="center" gap="s">
                  <AtiraIcon icon={faRobot} color="sub" />
                  <Name length={Lengths.NAME}>{chatbot.name}</Name>
                </Flex>
              </Flex>

              {chatbot?.description ? (
                <Description length={55}>{chatbot.description}</Description>
              ) : null}

              <ActionsRow>
                <Tooltip title={t("common.edit")}>
                  <IconActionButton
                    icon={faPenToSquare}
                    onClick={() => onEdit(chatbot)}
                    dataTestId={TestIDs.chatbot.list.actions.edit(chatbot._id)}
                  />
                </Tooltip>

                <Tooltip title={t("common.delete")}>
                  <IconActionButton
                    icon={faTrash}
                    onClick={() => {
                      setCurrentBot(chatbot);
                      setDeleteBotModalOpen(true);
                    }}
                    $isDanger
                    dataTestId={TestIDs.chatbot.list.actions.delete(
                      chatbot._id,
                    )}
                  />
                </Tooltip>

                <Tooltip title={t("common.share")}>
                  <IconActionButton
                    icon={faShare}
                    onClick={() => {
                      toggleShareDrawerOpen();
                      setCurrentBot(chatbot);
                    }}
                    dataTestId={TestIDs.chatbot.list.actions.share(chatbot._id)}
                  />
                </Tooltip>
              </ActionsRow>
            </BotCard>
          ))}
        </Flex>
      ) : (
        <AtiraEmpty
          description={t("settings.aiChatbot.empty.title")}
          action={{
            title: t("settings.aiChatbot.empty.button"),
            onClick: canCreateChatBot
              ? onCreate
              : () =>
                  setSubToUnlock({
                    visible: true,
                    title: t("subToUnlock.specific.chatBotPlanLimit"),
                  }),
            dataTestId: TestIDs.chatbot.empty.createButton,
          }}
        />
      )}

      <SettingsAIChatBotShareDrawer />
      <SettingsAIChatBotCreateEditDrawer />

      <WarningModal
        isOpen={deleteBotModalOpen}
        onClose={() => setDeleteBotModalOpen(false)}
        onConfirm={onDelete}
        description={t("settings.aiChatbot.delete.warning", {
          name: currentBot?.name,
        })}
        loading={deleteLoading}
      />
    </SettingsWrapper>
  );
};
